import {
  Children,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
} from "react";
import type { Components } from "react-markdown";
import { wrapStreamingText } from "./wrapStreamingText";

const CITATION_BTN =
  "font-semibold cursor-pointer text-xs leading-none text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 align-super ml-0.5 px-1 py-0.5 rounded transition-colors disabled:cursor-default disabled:hover:text-blue-500 disabled:hover:bg-blue-50";

const CITATION_LINK =
  "text-blue-700 hover:text-blue-900 underline cursor-pointer break-all";

const CITATION_RE = /(\[\d+(?:,\s*\d+)*\])/g;

interface ChatMarkdownContextValue {
  onCitationClick?: (id: number) => void;
  resolvedIds?: Set<number>;
  citationsByDocId?: Map<string, number>;
  fadeTokens: boolean;
  shownTextsRef: MutableRefObject<string[]>;
}

const ChatMarkdownContext = createContext<ChatMarkdownContextValue>({
  fadeTokens: false,
  shownTextsRef: { current: [] },
});

export const ChatMarkdownProvider = ({
  children,
  onCitationClick,
  resolvedIds,
  citationsByDocId,
  fadeTokens,
}: Omit<ChatMarkdownContextValue, "shownTextsRef"> & { children: ReactNode }) => {
  const shownTextsRef = useRef<string[]>([]);
  const wasFadingRef = useRef(fadeTokens);
  if (fadeTokens && !wasFadingRef.current) {
    shownTextsRef.current = [];
  }
  wasFadingRef.current = fadeTokens;

  useEffect(() => {
    if (!fadeTokens) shownTextsRef.current = [];
  }, [fadeTokens]);

  const value = useMemo(
    () => ({
      onCitationClick,
      resolvedIds,
      citationsByDocId,
      fadeTokens,
      shownTextsRef,
    }),
    [onCitationClick, resolvedIds, citationsByDocId, fadeTokens],
  );

  return (
    <ChatMarkdownContext.Provider value={value}>
      {children}
    </ChatMarkdownContext.Provider>
  );
};

const useChatMarkdown = (): ChatMarkdownContextValue =>
  useContext(ChatMarkdownContext);

/** Pull `bio_abc#L1...` display label + doc id from a citation href. */
const parseCitationHref = (
  href: string | undefined,
): { label: string; docId: string } | undefined => {
  if (!href) return undefined;
  let path: string | undefined;
  if (href.startsWith("/citations/")) {
    path = href;
  } else {
    const paperclip = href.match(
      /^https:\/\/paperclip(?:\.staging)?\.gxl\.ai(\/citations\/.+)$/,
    );
    if (paperclip) path = paperclip[1];
    else {
      const legacy = href.match(/^https:\/\/citations\.gxl\.ai(\/.+)$/);
      if (legacy) path = `/citations${legacy[1]}`;
    }
  }
  if (!path) return undefined;
  const m = path.match(/^\/citations\/[^/]+\/([^#/?]+)(#.*)?/);
  if (!m) return undefined;
  return { docId: m[1], label: `${m[1]}${m[2] ?? ""}` };
};

const CitationChip = ({
  id,
  onClick,
}: {
  id: number;
  onClick?: (id: number) => void;
}) => {
  const resolved = !!onClick;
  return (
    <button
      type="button"
      disabled={!resolved}
      aria-label={resolved ? `View citation ${id}` : `Citation ${id}`}
      onClick={
        onClick
          ? (e) => {
              e.preventDefault();
              onClick(id);
            }
          : undefined
      }
      className={CITATION_BTN}
    >
      {id}
    </button>
  );
};

const renderCitationId = (
  id: number,
  key: string,
  onClick: ((id: number) => void) | undefined,
  resolvedIds: Set<number> | undefined,
  fadeTokens: boolean,
): ReactNode => {
  if (onClick && resolvedIds?.has(id)) {
    return <CitationChip key={key} id={id} onClick={onClick} />;
  }
  // Streaming before this group's REFERENCES: same chip, not clickable.
  if (fadeTokens) {
    return <CitationChip key={key} id={id} />;
  }
  return <span key={key}>{`[${id}]`}</span>;
};

const processCitationRefs = (
  children: ReactNode,
  onClick: ((id: number) => void) | undefined,
  resolvedIds: Set<number> | undefined,
  fadeTokens: boolean,
): ReactNode => {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      const parts = child.split(CITATION_RE);
      if (parts.length === 1) return child;

      return parts.map((part, i) => {
        const m = /^\[(\d+(?:,\s*\d+)*)\]$/.exec(part);
        if (!m) return part;

        const ids = m[1].split(",").map((s) => parseInt(s.trim(), 10));
        return ids.map((id) =>
          renderCitationId(id, `cit-${i}-${id}`, onClick, resolvedIds, fadeTokens),
        );
      });
    }

    if (
      child != null &&
      typeof child === "object" &&
      "props" in (child as ReactElement)
    ) {
      const el = child as ReactElement<{ children?: ReactNode }>;
      if (el.props?.children) {
        return {
          ...el,
          props: {
            ...el.props,
            children: processCitationRefs(
              el.props.children,
              onClick,
              resolvedIds,
              fadeTokens,
            ),
          },
        };
      }
    }

    return child;
  });
};

const withCitations = (
  children: ReactNode,
  onClick?: (id: number) => void,
  resolvedIds?: Set<number>,
  fadeTokens = false,
): ReactNode => {
  if (!onClick && !resolvedIds && !fadeTokens) return children;
  return processCitationRefs(children, onClick, resolvedIds, fadeTokens);
};

const Cited = ({ children }: { children?: ReactNode }) => {
  const { onCitationClick, resolvedIds, fadeTokens, shownTextsRef } =
    useChatMarkdown();
  const withCites = withCitations(
    children,
    onCitationClick,
    resolvedIds,
    fadeTokens,
  );
  return fadeTokens
    ? wrapStreamingText(withCites, shownTextsRef)
    : withCites;
};

const ChatLink = ({
  href,
  children,
  ...props
}: {
  href?: string;
  children?: ReactNode;
}) => {
  const { onCitationClick, resolvedIds, citationsByDocId, fadeTokens } =
    useChatMarkdown();
  const citMatch =
    typeof children === "string" && /^\[(\d+)\]$/.exec(children);
  if (citMatch) {
    const citId = parseInt(citMatch[1], 10);
    if (onCitationClick && resolvedIds?.has(citId)) {
      return <CitationChip id={citId} onClick={onCitationClick} />;
    }
    if (fadeTokens) {
      return <CitationChip id={citId} />;
    }
    return <span>{children}</span>;
  }
  const parsed = parseCitationHref(href);
  if (parsed) {
    const citId = citationsByDocId?.get(parsed.docId);
    if (citId != null && onCitationClick && resolvedIds?.has(citId)) {
      return (
        <button
          type="button"
          aria-label={`View citation ${citId}`}
          onClick={(e) => {
            e.preventDefault();
            onCitationClick(citId);
          }}
          className={CITATION_LINK}
        >
          {parsed.label}
        </button>
      );
    }
    return <span className="break-all">{parsed.label}</span>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-700 hover:text-blue-900 underline"
      {...props}
    >
      {children}
      <span className="sr-only"> (opens in new tab)</span>
    </a>
  );
};

const ChatTh = ({
  children,
  style,
}: {
  children?: ReactNode;
  style?: CSSProperties;
}) => {
  const { onCitationClick, resolvedIds, fadeTokens } = useChatMarkdown();
  return (
    <th
      className="border border-zinc-200 px-3 py-2 font-semibold text-left text-xs"
      style={style}
    >
      {withCitations(children, onCitationClick, resolvedIds, fadeTokens)}
    </th>
  );
};

const ChatTd = ({
  children,
  style,
}: {
  children?: ReactNode;
  style?: CSSProperties;
}) => {
  const { onCitationClick, resolvedIds, fadeTokens } = useChatMarkdown();
  return (
    <td className="border border-zinc-200 px-3 py-2 text-sm" style={style}>
      {withCitations(children, onCitationClick, resolvedIds, fadeTokens)}
    </td>
  );
};

/** Stable component map — reads citation/fade state from context so identity never changes. */
export const chatMarkdownComponents: Components = {
  h1: ({ children }) => (
    <h2 className="mt-4 mb-2 text-xl font-semibold first:mt-0">
      <Cited>{children}</Cited>
    </h2>
  ),
  h2: ({ children }) => (
    <h2 className="mt-3 mb-2 text-lg font-semibold first:mt-0">
      <Cited>{children}</Cited>
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-2 mb-1 text-base font-semibold first:mt-0">
      <Cited>{children}</Cited>
    </h3>
  ),
  p: ({ children }) => (
    <p className="mb-3 leading-relaxed last:mb-0 break-words">
      <Cited>{children}</Cited>
    </p>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 list-disc space-y-0.5 pl-5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 list-decimal space-y-0.5 pl-5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed break-words">
      <Cited>{children}</Cited>
    </li>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-zinc-100 rounded px-1 py-0.5 font-mono text-xs break-words">
        {children}
      </code>
    ) : (
      <code className="bg-zinc-100 block overflow-x-auto rounded p-2 font-mono text-xs whitespace-pre-wrap break-words">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-zinc-100 mb-3 overflow-x-auto rounded-[3px] p-3 max-w-full text-xs">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-zinc-300 text-zinc-600 mb-4 mt-4 ml-6 mr-6 md:mr-24 border-l-2 pl-3 italic text-sm">
      <Cited>{children}</Cited>
    </blockquote>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">
      <Cited>{children}</Cited>
    </strong>
  ),
  hr: () => <hr className="my-4 border-zinc-200" />,
  em: ({ children }) => (
    <em className="italic">
      <Cited>{children}</Cited>
    </em>
  ),
  a: ChatLink,
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="min-w-full border-collapse border border-zinc-200 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-zinc-50">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-zinc-200">{children}</tr>
  ),
  th: ChatTh,
  td: ChatTd,
};
